import type { AnnotateCurrentUser, DrawerCreatedByFilter, DrawerPageScope, DrawerSortOrder, DrawerStatusFilter, InstrucktConfig, MarkerRecord, PendingAnnotation, ProjectMember, TestingItem, TestingItemPriority, TestingItemStatus } from './types'
import { InstrucktApi, isAuthError } from './api'
import type { CreateTestingItemPayload, UpdateTestingItemPayload } from './api'
import { Toolbar } from './ui/toolbar'
import { ElementHighlight } from './ui/highlight'
import { AnnotationPopup } from './ui/popup'
import type { PopupCreateItemResult } from './ui/popup'
import { AnnotationMarkers } from './ui/markers'
import { IssueDrawer } from './ui/drawer'
import { injectGlobalStyles } from './ui/styles'
import { getElementSelector, getElementName, getElementLabel, getNearbyText, getCssClasses, getPageBoundingBox } from './selector'
import { captureRegion, selectRegion, setPreferScreenCapture } from './ui/screenshot'
import { ConsoleCapture } from './capture/console'
import { NetworkCapture } from './capture/network'
import { resolveElementInfo as resolveElementSource } from 'element-source'
import Bowser from 'bowser'
import type { FrameworkContext, SourceFrame } from './types'
import * as livewireAdapter from './adapters/livewire'
import * as vueAdapter from './adapters/vue'
import * as svelteAdapter from './adapters/svelte'
import * as reactAdapter from './adapters/react'
import * as bladeAdapter from './adapters/blade'

const DEFAULT_TOKEN_STORAGE_KEY = 'visual-qa:token'
const DEFAULT_TOKEN_QUERY_PARAMS = ['enactx_annotate_token', 'access_token', 'token', 'visual_qa_token']
const ACTIONABLE_STATUSES: TestingItemStatus[] = ['open', 'in_progress', 'reopened']
const TOOL_ENABLED_STORAGE_KEY = 'visual-qa:enabled'

function pageKey(): string {
  return window.location.pathname
}

function currentPageUrl(): string {
  return `${window.location.origin}${pageKey()}`
}

function currentDomain(): string {
  return window.location.hostname
}

function matchesKeyBinding(event: KeyboardEvent, binding: string): boolean {
  const parts = binding.toLowerCase().split('+').map(part => part.trim()).filter(Boolean)
  const key = parts.pop()
  if (!key) return false

  const needsMeta = parts.includes('meta')
  const needsCtrl = parts.includes('ctrl') || parts.includes('control')
  const needsAlt = parts.includes('alt')
  const needsShift = parts.includes('shift')
  const needsControlOrMeta = parts.includes('controlormeta') || parts.includes('cmdorctrl')

  if (event.shiftKey !== needsShift) return false
  if (event.altKey !== needsAlt) return false
  if (needsControlOrMeta) {
    if (event.ctrlKey === event.metaKey) return false
  } else {
    if (event.ctrlKey !== needsCtrl) return false
    if (event.metaKey !== needsMeta) return false
  }

  if (key.length === 1 && /^[a-z0-9]$/i.test(key)) {
    return event.code === `Key${key.toUpperCase()}`
  }

  return event.key.toLowerCase() === key
}

function uniqueItems(items: TestingItem[]): TestingItem[] {
  const seen = new Map<string, TestingItem>()
  for (const item of items) seen.set(item.id, item)
  return [...seen.values()]
}

function titleCase(value: string): string {
  return value.split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
}

export class Instruckt {
  private config: Required<Pick<InstrucktConfig, 'endpoint' | 'theme' | 'position'>> & InstrucktConfig
  private api: InstrucktApi
  private toolEnabled: boolean
  private toolbar: Toolbar | null = null
  private highlight: ElementHighlight | null = null
  private popup: AnnotationPopup | null = null
  private markers: AnnotationMarkers | null = null
  private drawer: IssueDrawer | null = null
  private consoleCapture = new ConsoleCapture()
  private networkCapture: NetworkCapture
  private pageItems: TestingItem[] = []
  private projectItems: TestingItem[] = []
  private members: ProjectMember[] = []
  private currentUser: AnnotateCurrentUser | null = null
  private assigneeFilter: 'me' | 'all' | 'unassigned' | number = 'me'
  private pageScope: DrawerPageScope = 'all'
  private statusFilter: DrawerStatusFilter = 'open'
  private createdByFilter: DrawerCreatedByFilter = 'all'
  private sortOrder: DrawerSortOrder = 'newest'
  
  private visibleItemId: string | null = null
  private isAnnotating = false
  private isFrozen = false
  private frozenStyleEl: HTMLStyleElement | null = null
  private frozenPopovers: { el: HTMLElement; original: string }[] = []
  private rafId: number | null = null
  private pendingMouseTarget: Element | null = null
  private highlightLocked = false
  private initialLoadDone = false
  private initialLoad: Promise<void> | null = null
  private initialDataLoading = false
  private listReloadLoading = false
  private detailLoadingItemId: string | null = null
  private detailErrorItemId: string | null = null
  private detailRequestsInFlight = new Set<string>()
  private pendingSelectedItemId: string | null = null
  private authPromptActive = false
  private authPromptDismissed = false
  private lastRouteKey = pageKey()
  private navigationRefreshQueued = false
  private readonly originalPushState = history.pushState.bind(history)
  private readonly originalReplaceState = history.replaceState.bind(history)
  private boundKeydown: (e: KeyboardEvent) => void
  private boundLivewireNavigated = (): void => {
    this.queueNavigationRefresh(true)
  }
  private boundInertiaNavigate = (): void => {
    this.queueNavigationRefresh()
  }
  private boundPopstate = (): void => { this.queueNavigationRefresh() }
  private boundReposition = (): void => {
    this.markers?.reposition(this.visibleMarkers())
    this.syncVisibleItemHighlight()
  }

  constructor(config: InstrucktConfig) {
    this.config = {
      adapters: ['livewire', 'vue', 'svelte', 'react', 'blade'],
      authMode: 'none',
      theme: 'auto',
      position: 'bottom-right',
      ...config,
    }
    this.api = new InstrucktApi(config.endpoint, {
      getAccessToken: () => this.getAccessToken(),
    })
    this.toolEnabled = config.startEnabled ?? this.config.authMode !== 'crm-redirect'
    this.networkCapture = new NetworkCapture(this.config.endpoint)
    this.boundKeydown = this.onKeydown.bind(this)
    this.init()
  }

  private init(): void {
    this.consumeAccessTokenFromUrl()
    this.loadDrawerPrefs()
    if (this.config.startEnabled === undefined && this.isRedirectAuthMode()) {
      this.toolEnabled = false
    }
    injectGlobalStyles(this.config.colors)
    this.consoleCapture.start()
    this.networkCapture.start()

    const adapters = this.config.adapters ?? []
    if (adapters.includes('livewire') && livewireAdapter.isAvailable()) setPreferScreenCapture(true)
    if (this.config.theme !== 'auto') document.documentElement.setAttribute('data-visual-qa-theme', this.config.theme)

    this.toolbar = new Toolbar(this.config.position, {
      onToggleAnnotate: active => this.setAnnotating(active),
      onFreezeAnimations: frozen => this.setFrozen(frozen),
      onScreenshot: () => this.startRegionCapture(),
      onCopy: () => this.copyToClipboard(true),
      onToggleIssues: active => this.toggleItemDrawer(active),
      onClearPage: () => { void this.clearPage() },
      onClearAll: () => { void this.clearEverything() },
      onPopOut: () => { void this.popOutDrawer() },
    }, this.config.keys, this.config.tools)

    this.highlight = new ElementHighlight()
    this.popup = new AnnotationPopup()
    this.markers = new AnnotationMarkers(marker => this.onMarkerClick(marker))
    this.drawer = new IssueDrawer({
      onOpenItem: itemId => this.setVisibleItem(itemId),
      onUpdateItem: (itemId, patch) => this.updateItem(itemId, patch),
      onAddComment: (itemId, message) => this.addComment(itemId, message),
      onEditComment: (itemId, commentId, message) => this.editComment(itemId, commentId, message),
      onResolveItem: (itemId, resolution) => this.resolveItem(itemId, resolution),
      onCopy: itemId => this.copyToClipboard(true, itemId),
      onChangeAssigneeFilter: value => { void this.changeAssigneeFilter(value) },
      onChangePageScope: value => this.changePageScope(value),
      onChangeStatusFilter: value => { void this.changeStatusFilter(value) },
      onChangeCreatedByFilter: value => { void this.changeCreatedByFilter(value) },
      onChangeSortOrder: value => this.changeSortOrder(value),
      onClose: () => {
        this.visibleItemId = null
        this.toolbar?.setIssuesActive(false)
        this.syncVisibleItemHighlight()
      },
      onPipClosed: () => this.redockAfterPip(),
    })

    document.addEventListener('keydown', this.boundKeydown)
    window.addEventListener('scroll', this.boundReposition, { passive: true })
    window.addEventListener('resize', this.boundReposition, { passive: true })
    document.addEventListener('livewire:navigated', this.boundLivewireNavigated)
    document.addEventListener('inertia:navigate', this.boundInertiaNavigate)
    window.addEventListener('popstate', this.boundPopstate)
    history.pushState = ((data: unknown, unused: string, url?: string | URL | null) => {
      this.originalPushState(data, unused, url)
      this.queueNavigationRefresh()
    }) as History['pushState']
    history.replaceState = ((data: unknown, unused: string, url?: string | URL | null) => {
      this.originalReplaceState(data, unused, url)
      this.queueNavigationRefresh()
    }) as History['replaceState']

    this.applyToolEnabledState()
    if (this.toolEnabled) this.startInitialLoads()
  }

  private startInitialLoads(): void {
    if (this.initialLoad || this.initialLoadDone) return
    this.initialDataLoading = true
    this.initialLoad = this.loadInitialData()
  }

  private async loadInitialData(): Promise<void> {
    try {
      await Promise.all([this.loadCurrentUser(), this.loadPageItems(), this.loadProjectItems(), this.loadMembers()])
      this.initialLoadDone = true
    } finally {
      this.initialDataLoading = false
      this.initialLoad = null
      this.syncUi()
    }
  }

  private makeToolbarCallbacks() {
    return {
      onToggleAnnotate: (active: boolean) => this.setAnnotating(active),
      onFreezeAnimations: (frozen: boolean) => this.setFrozen(frozen),
      onScreenshot: () => this.startRegionCapture(),
      onCopy: () => this.copyToClipboard(true),
      onToggleIssues: (active: boolean) => this.toggleItemDrawer(active),
      onClearPage: () => { void this.clearPage() },
      onClearAll: () => { void this.clearEverything() },
    }
  }

  private reattach(): void {
    const wasAnnotating = this.isAnnotating
    if (this.isAnnotating) this.detachAnnotateListeners()
    if (this.isFrozen) this.setFrozen(false)
    this.isAnnotating = false
    this.isFrozen = false

    this.toolbar?.destroy()
    this.highlight?.destroy()
    this.popup?.destroy()
    this.markers?.destroy()
    this.drawer?.destroy()
    document.querySelectorAll('[data-visual-qa]').forEach(el => el.remove())

    this.toolbar = new Toolbar(this.config.position, this.makeToolbarCallbacks(), this.config.keys, this.config.tools)

    this.markers = new AnnotationMarkers(marker => this.onMarkerClick(marker))
    this.highlight = new ElementHighlight()
    this.drawer = new IssueDrawer({
      onOpenItem: itemId => this.setVisibleItem(itemId),
      onUpdateItem: (itemId, patch) => this.updateItem(itemId, patch),
      onAddComment: (itemId, message) => this.addComment(itemId, message),
      onEditComment: (itemId, commentId, message) => this.editComment(itemId, commentId, message),
      onResolveItem: (itemId, resolution) => this.resolveItem(itemId, resolution),
      onCopy: itemId => this.copyToClipboard(true, itemId),
      onChangeAssigneeFilter: value => { void this.changeAssigneeFilter(value) },
      onChangePageScope: value => this.changePageScope(value),
      onChangeStatusFilter: value => { void this.changeStatusFilter(value) },
      onChangeCreatedByFilter: value => { void this.changeCreatedByFilter(value) },
      onChangeSortOrder: value => this.changeSortOrder(value),
      onClose: () => {
        this.visibleItemId = null
        this.toolbar?.setIssuesActive(false)
        this.syncVisibleItemHighlight()
      },
      onPipClosed: () => this.redockAfterPip(),
    })

    const existing = document.getElementById('visual-qa-global')
    if (existing) existing.remove()
    injectGlobalStyles(this.config.colors)
    this.syncUi()
    if (wasAnnotating) this.setAnnotating(true)
  }

  private queueNavigationRefresh(needsReattach = false): void {
    if (this.navigationRefreshQueued) return
    this.navigationRefreshQueued = true
    setTimeout(() => {
      this.navigationRefreshQueued = false
      const nextRouteKey = pageKey()
      if (nextRouteKey === this.lastRouteKey && !needsReattach) return
      this.lastRouteKey = nextRouteKey
      if (needsReattach) this.reattach()
      void this.refreshForNavigation()
    }, 0)
  }

  private async refreshForNavigation(): Promise<void> {
    if (!this.toolEnabled) return
    this.visibleItemId = null
    this.pendingSelectedItemId = null
    this.detailLoadingItemId = null
    this.detailErrorItemId = null
    this.pageItems = []
    this.syncUi()
    try {
      await Promise.all([this.loadPageItems(), this.loadProjectItems()])
      this.syncUi()
    } catch (error) {
      if (this.handleAuthError(error)) return
      throw error
    }
  }

  async refresh(): Promise<void> {
    try {
      await Promise.all([this.loadCurrentUser(), this.loadPageItems(), this.loadProjectItems()])
      this.syncUi()
    } catch (error) {
      if (this.handleAuthError(error)) return
      throw error
    }
  }

  private async changeAssigneeFilter(value: 'me' | 'all' | 'unassigned' | number): Promise<void> {
    this.assigneeFilter = value
    this.saveDrawerPrefs()
    this.syncUi()
  }

  private changePageScope(value: DrawerPageScope): void {
    if (this.pageScope === value) return
    this.pageScope = value
    this.saveDrawerPrefs()
    this.syncUi()
  }

  private async changeStatusFilter(value: DrawerStatusFilter): Promise<void> {
    if (this.statusFilter === value) return
    this.statusFilter = value
    this.saveDrawerPrefs()
    await this.reloadItemLists()
  }

  private async changeCreatedByFilter(value: DrawerCreatedByFilter): Promise<void> {
    if (this.createdByFilter === value) return
    this.createdByFilter = value
    this.saveDrawerPrefs()
    await this.reloadItemLists()
  }

  private changeSortOrder(value: DrawerSortOrder): void {
    if (this.sortOrder === value) return
    this.sortOrder = value
    this.saveDrawerPrefs()
    this.syncUi()
  }

  private drawerPrefsStorageKey(): string {
    return `visual-qa:drawer-prefs:${this.config.projectId ?? 'default'}`
  }

  private loadDrawerPrefs(): void {
    try {
      const raw = localStorage.getItem(this.drawerPrefsStorageKey())
      if (!raw) return
      const prefs = JSON.parse(raw) as Partial<{
        assigneeFilter: 'me' | 'all' | 'unassigned' | number
        pageScope: DrawerPageScope
        statusFilter: DrawerStatusFilter
        createdByFilter: DrawerCreatedByFilter
        sortOrder: DrawerSortOrder
      }>
      if (prefs.assigneeFilter !== undefined) this.assigneeFilter = prefs.assigneeFilter
      if (prefs.pageScope) this.pageScope = prefs.pageScope
      if (prefs.statusFilter) this.statusFilter = prefs.statusFilter
      if (prefs.createdByFilter !== undefined) this.createdByFilter = prefs.createdByFilter
      if (prefs.sortOrder) this.sortOrder = prefs.sortOrder
    } catch {}
  }

  private saveDrawerPrefs(): void {
    try {
      localStorage.setItem(this.drawerPrefsStorageKey(), JSON.stringify({
        assigneeFilter: this.assigneeFilter,
        pageScope: this.pageScope,
        statusFilter: this.statusFilter,
        createdByFilter: this.createdByFilter,
        sortOrder: this.sortOrder,
      }))
    } catch {}
  }

  private statusFilterParam(): string | undefined {
    if (this.statusFilter === 'all') return undefined
    return this.statusFilter
  }

  private listQueryParams() {
    return {
      status: this.statusFilterParam(),
      createdBy: this.createdByFilter === 'all' ? undefined : this.createdByFilter,
    }
  }

  private async reloadItemLists(): Promise<void> {
    this.listReloadLoading = true
    this.syncUi()
    try {
      await Promise.all([this.loadPageItems(), this.loadProjectItems()])
    } finally {
      this.listReloadLoading = false
      this.syncUi()
    }
  }

  private tokenStorageKey(): string {
    return this.config.tokenStorageKey ?? DEFAULT_TOKEN_STORAGE_KEY
  }

  private toolEnabledStorageKey(): string {
    return `${TOOL_ENABLED_STORAGE_KEY}:${this.config.projectId ?? 'default'}`
  }

  private storeToolEnabled(enabled: boolean): void {
    try {
      localStorage.setItem(this.toolEnabledStorageKey(), String(enabled))
    } catch {}
  }

  private getTokenQueryParams(): string[] {
    if (this.config.tokenQueryParam) return [this.config.tokenQueryParam]
    return DEFAULT_TOKEN_QUERY_PARAMS
  }

  private getAccessToken(): string | null {
    try {
      const key = this.tokenStorageKey()
      const storedToken = localStorage.getItem(key)
      if (storedToken) return storedToken

      const sessionToken = sessionStorage.getItem(key)
      if (!sessionToken) return null

      localStorage.setItem(key, sessionToken)
      sessionStorage.removeItem(key)
      return sessionToken
    } catch {
      return null
    }
  }

  private storeAccessToken(token: string): void {
    this.authPromptDismissed = false
    localStorage.setItem(this.tokenStorageKey(), token)
  }

  private clearAccessToken(): void {
    try {
      localStorage.removeItem(this.tokenStorageKey())
      sessionStorage.removeItem(this.tokenStorageKey())
    } catch {}
  }

  private consumeAccessTokenFromUrl(): void {
    const url = new URL(window.location.href)
    let token: string | null = null
    for (const key of this.getTokenQueryParams()) {
      const value = url.searchParams.get(key)?.trim()
      if (!value) continue
      token = value
      url.searchParams.delete(key)
      break
    }
    if (!token) return
    this.storeAccessToken(token)
    history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`)
  }

  private isRedirectAuthMode(): boolean {
    return this.config.authMode === 'crm-redirect'
  }

  private crmAuthorizeUrl(): string {
    if (!this.config.crmBase) throw new Error('visual-qa: crmBase is required for redirect auth')
    if (!this.config.projectId) throw new Error('visual-qa: projectId is required for redirect auth')
    const url = new URL('/oauth/authorize', this.config.crmBase)
    url.searchParams.set('annotate', 'true')
    url.searchParams.set('project_id', this.config.projectId)
    url.searchParams.set('redirect_uri', window.location.href)
    return url.toString()
  }

  private requireAuth(): never {
    window.location.assign(this.crmAuthorizeUrl())
    throw new Error('visual-qa: redirecting for auth')
  }

  private promptForAuth(force = false): never | false {
    if (this.authPromptActive) return false
    if (!force && this.authPromptDismissed) return false

    this.authPromptActive = true
    const shouldLogin = window.confirm('Visual QA requires CRM login. Login now?')
    this.authPromptActive = false
    if (!shouldLogin) {
      this.authPromptDismissed = true
      return false
    }

    this.authPromptDismissed = false
    this.requireAuth()
  }

  private ensureAuthForEnable(): boolean {
    if (!this.isRedirectAuthMode()) return true
    if (this.getAccessToken()) return true
    return this.promptForAuth(true)
  }

  private handleAuthError(error: unknown): boolean {
    if (!this.isRedirectAuthMode() || !isAuthError(error)) return false
    this.clearAccessToken()
    if (this.toolEnabled) this.setToolEnabled(false)
    return true
  }

  private applyToolEnabledState(): void {
    this.toolbar?.setVisible(this.toolEnabled)
    this.markers?.setVisible(this.toolEnabled)
    if (!this.toolEnabled) {
      if (this.isAnnotating) this.setAnnotating(false)
      if (this.isFrozen) this.setFrozen(false)
      this.toolbar?.setIssuesActive(false)
      this.drawer?.close()
      this.popup?.destroy()
      this.highlight?.hide()
      this.visibleItemId = null
    }
  }

  private setToolEnabled(enabled: boolean): void {
    if (enabled === this.toolEnabled) return
    if (enabled && !this.ensureAuthForEnable()) return
    this.toolEnabled = enabled
    if (this.config.startEnabled === undefined && !this.isRedirectAuthMode()) {
      this.storeToolEnabled(enabled)
    }
    if (enabled) this.startInitialLoads()
    this.applyToolEnabledState()
    if (enabled) this.syncUi()
  }

  private requireProjectId(): string {
    if (!this.config.projectId) throw new Error('visual-qa: projectId is required')
    return this.config.projectId
  }

  private async loadPageItems(): Promise<void> {
    try {
      this.pageItems = this.mergeLoadedItems(this.pageItems, await this.loadAllItems({
        projectId: this.requireProjectId(),
        url: currentPageUrl(),
        domain: currentDomain(),
        ...this.listQueryParams(),
      }))
    } catch (error) {
      if (this.handleAuthError(error)) return
      throw error
    }
  }

  private async loadProjectItems(): Promise<void> {
    try {
      this.projectItems = this.mergeLoadedItems(this.projectItems, await this.loadAllItems({
        projectId: this.requireProjectId(),
        domain: currentDomain(),
        ...this.listQueryParams(),
      }))
    } catch (error) {
      if (this.handleAuthError(error)) return
      throw error
    }
  }

  private async loadAllItems(params: { projectId: string; domain: string; url?: string; status?: string; createdBy?: 'me' | number }): Promise<TestingItem[]> {
    const firstPage = await this.api.listItems({ ...params, page: 1, limit: 100 })
    const totalPages = Math.max(1, Math.ceil(firstPage.total / firstPage.limit))
    if (totalPages === 1) return firstPage.items

    const remainingPages = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) => this.api.listItems({
        ...params,
        page: index + 2,
        limit: firstPage.limit,
      })),
    )
    return uniqueItems([firstPage, ...remainingPages].flatMap(response => response.items))
  }

  private mergeLoadedItems(current: TestingItem[], incoming: TestingItem[]): TestingItem[] {
    const existing = new Map(current.map(item => [item.id, item]))
    return incoming.map(item => this.mergeItem(existing.get(item.id), item))
  }

  private mergeItem(existing: TestingItem | undefined, incoming: TestingItem): TestingItem {
    if (!existing) return incoming
    if (incoming.hasFullDetails === false && existing.hasFullDetails) {
      return {
        ...existing,
        ...incoming,
        comments: existing.comments,
        description: incoming.description ?? existing.description,
        screenshot: incoming.screenshot ?? existing.screenshot,
        element: incoming.element ?? existing.element,
        selectorPath: incoming.selectorPath ?? existing.selectorPath,
        coordinates: incoming.coordinates ?? existing.coordinates,
        consoleLog: incoming.consoleLog && incoming.consoleLog.length > 0 ? incoming.consoleLog : existing.consoleLog,
        userAgent: incoming.userAgent ?? existing.userAgent,
        browser: incoming.browser ?? existing.browser,
        device: incoming.device ?? existing.device,
        os: incoming.os ?? existing.os,
        viewportWidth: incoming.viewportWidth ?? existing.viewportWidth,
        viewportHeight: incoming.viewportHeight ?? existing.viewportHeight,
        networkRequests: incoming.networkRequests && incoming.networkRequests.length > 0 ? incoming.networkRequests : existing.networkRequests,
        resolution: incoming.resolution ?? existing.resolution,
        hasFullDetails: existing.hasFullDetails,
      }
    }
    return { ...existing, ...incoming }
  }

  private async revalidateAfterMutation(itemId: string): Promise<void> {
    await Promise.all([this.loadPageItems(), this.loadProjectItems()])
    if (this.visibleItemId === itemId && !this.allItems().some(item => item.id === itemId)) {
      this.visibleItemId = null
    }
  }

  private async loadMembers(): Promise<void> {
    try {
      this.members = await this.api.listMembers(this.requireProjectId())
    } catch (error) {
      if (this.handleAuthError(error)) return
      this.members = []
    }
  }

  private async loadCurrentUser(): Promise<void> {
    try {
      this.currentUser = await this.api.getMe()
    } catch (error) {
      if (this.handleAuthError(error)) return
      this.currentUser = null
    }
  }

  private async ensureItemDetail(itemId: string, showDrawerLoading = this.visibleItemId === itemId): Promise<void> {
    const existing = this.allItems().find(item => item.id === itemId)
    if (existing?.hasFullDetails) {
      if (this.detailErrorItemId === itemId) this.detailErrorItemId = null
      if (this.detailLoadingItemId === itemId) this.detailLoadingItemId = null
      return
    }
    if (this.detailRequestsInFlight.has(itemId)) return
    this.detailRequestsInFlight.add(itemId)
    if (showDrawerLoading) this.detailLoadingItemId = itemId
    if (this.detailErrorItemId === itemId) this.detailErrorItemId = null
    if (showDrawerLoading) this.syncUi()
    try {
      const detailed = await this.api.getItemDetail(this.requireProjectId(), itemId)
      if (this.detailErrorItemId === itemId) this.detailErrorItemId = null
      this.updateLocalItem(detailed)
    } catch (error) {
      if (this.handleAuthError(error)) return
      if (showDrawerLoading) this.detailErrorItemId = itemId
    } finally {
      this.detailRequestsInFlight.delete(itemId)
      if (this.detailLoadingItemId === itemId) this.detailLoadingItemId = null
      this.syncUi()
    }
  }

  private actionableItems(items = this.allItems()): TestingItem[] {
    return items.filter(item => ACTIONABLE_STATUSES.includes(item.status))
  }

  private allItems(): TestingItem[] {
    return uniqueItems([...this.projectItems, ...this.pageItems]).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  }

  private matchesAssigneeFilter(item: TestingItem): boolean {
    if (this.assigneeFilter === 'all') return true
    if (this.assigneeFilter === 'unassigned') return item.assignedTo == null
    if (this.assigneeFilter === 'me') return this.currentUser ? item.assignedTo === this.currentUser.id : false
    return item.assignedTo === this.assigneeFilter
  }

  private drawerItems(selectedItemId: string | null = this.visibleItemId): TestingItem[] {
    const items = this.pageScope === 'page'
      ? this.allItems().filter(item => this.itemPageKey(item) === pageKey())
      : this.allItems()
    const filteredItems = this.sortItems(items.filter(item => this.matchesDrawerFilters(item)))
    if (!selectedItemId || filteredItems.some(item => item.id === selectedItemId)) return filteredItems
    const selectedItem = this.allItems().find(item => item.id === selectedItemId)
    return selectedItem ? [selectedItem, ...filteredItems] : filteredItems
  }

  private sortItems(items: TestingItem[]): TestingItem[] {
    const priorityRank: Record<TestingItemPriority, number> = { critical: 0, high: 1, medium: 2, low: 3 }
    const sorted = [...items]
    switch (this.sortOrder) {
      case 'oldest':
        return sorted.sort((a, b) => a.createdAt.localeCompare(b.createdAt))
      case 'recently_updated':
        return sorted.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      case 'priority':
        return sorted.sort((a, b) => priorityRank[a.priority] - priorityRank[b.priority] || b.createdAt.localeCompare(a.createdAt))
      default:
        return sorted.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    }
  }

  private matchesDrawerFilters(item: TestingItem): boolean {
    return this.matchesServerFilters(item) && this.matchesAssigneeFilter(item)
  }

  private matchesServerFilters(item: TestingItem): boolean {
    const statusParam = this.statusFilterParam()
    const matchesStatus = !statusParam || statusParam.split(',').includes(item.status)
    const matchesCreatedBy = this.createdByFilter === 'all'
      ? true
      : this.createdByFilter === 'me'
        ? this.currentUser ? item.createdBy === this.currentUser.id : false
        : item.createdBy === this.createdByFilter
    return matchesStatus && matchesCreatedBy
  }

  private itemPageKey(item: TestingItem): string {
    if (item.coordinates?.pagePath) return item.coordinates.pagePath
    try {
      return new URL(item.url).pathname
    } catch {
      return item.url
    }
  }

  private visibleMarkers(): MarkerRecord[] {
    const current = pageKey()
    const items = this.drawerItems(null).filter(item => this.itemPageKey(item) === current)
    for (const item of items) {
      if (!item.selectorPath && !item.hasFullDetails) void this.ensureItemDetail(item.id, false)
    }
    return items
      .flatMap(item => item.coordinates ? [{
        id: item.id,
        title: item.title,
        x: item.coordinates.x,
        y: item.coordinates.y,
        selectorPath: item.selectorPath,
        anchorXRatio: item.coordinates.anchorXRatio,
        anchorYRatio: item.coordinates.anchorYRatio,
        status: item.status,
        active: item.id === this.visibleItemId,
      }] : [])
  }

  private pageMarkerCount(): number {
    return this.visibleMarkers().length
  }

  private totalActionableCount(): number {
    return this.actionableItems().length
  }

  private syncUi(): void {
    const markers = this.visibleMarkers()
    if (this.visibleItemId && !this.allItems().find(item => item.id === this.visibleItemId)) {
      this.visibleItemId = null
    }
    this.markers?.sync(markers)
    this.syncVisibleItemHighlight()
    this.toolbar?.setAnnotationCount(this.pageMarkerCount())
    this.toolbar?.setTotalCount(this.totalActionableCount())
    this.drawer?.update(this.drawerItems(), this.members, this.visibleItemId, this.config.endpoint, this.assigneeFilter, this.pageScope, this.statusFilter, this.createdByFilter, this.sortOrder, this.currentUser?.name ?? 'Me', this.initialDataLoading || this.listReloadLoading, this.detailLoadingItemId, this.detailErrorItemId)
    this.openPendingItemSelection()
  }

  private openPendingItemSelection(): void {
    if (!this.pendingSelectedItemId) return
    const item = this.allItems().find(entry => entry.id === this.pendingSelectedItemId)
    if (!item || this.itemPageKey(item) !== pageKey()) return
    this.pendingSelectedItemId = null
    this.setVisibleItem(item.id)
  }

  private setVisibleItem(itemId: string | null): void {
    this.visibleItemId = itemId
    this.markers?.sync(this.visibleMarkers())
    if (itemId) {
      if (this.detailErrorItemId === itemId) this.detailErrorItemId = null
      const selectedItem = this.allItems().find(item => item.id === itemId)
      this.detailLoadingItemId = selectedItem?.hasFullDetails ? null : itemId
      this.drawer?.show(this.drawerItems(itemId), this.members, itemId, this.config.endpoint, this.assigneeFilter, this.pageScope, this.statusFilter, this.createdByFilter, this.sortOrder, this.currentUser?.name ?? 'Me', this.initialDataLoading || this.listReloadLoading, this.detailLoadingItemId, this.detailErrorItemId)
      this.toolbar?.setIssuesActive(true)
      void this.ensureItemDetail(itemId)
      this.syncVisibleItemHighlight()
      const target = this.resolveItemElement(selectedItem)
      target?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
      return
    }
    this.detailErrorItemId = null
    this.detailLoadingItemId = null
    if (this.drawer?.isOpen()) this.drawer.show(this.drawerItems(null), this.members, null, this.config.endpoint, this.assigneeFilter, this.pageScope, this.statusFilter, this.createdByFilter, this.sortOrder, this.currentUser?.name ?? 'Me', this.initialDataLoading || this.listReloadLoading, this.detailLoadingItemId, this.detailErrorItemId)
    this.syncVisibleItemHighlight()
  }

  private resolveItemElement(item: TestingItem | null | undefined): Element | null {
    if (!item?.selectorPath) return null
    try {
      const target = document.querySelector(item.selectorPath)
      return target instanceof Element ? target : null
    } catch {
      return null
    }
  }

  private syncVisibleItemHighlight(): void {
    if (this.highlightLocked) return
    const item = this.visibleItemId ? this.allItems().find(entry => entry.id === this.visibleItemId) : null
    const target = this.resolveItemElement(item)
    if (target) {
      this.highlight?.show(target)
      return
    }
    if (!this.isAnnotating) this.highlight?.hide()
  }

  private updateLocalItem(updated: TestingItem): void {
    const upsert = (items: TestingItem[]) => {
      const index = items.findIndex(item => item.id === updated.id)
      if (index >= 0) {
        items[index] = this.mergeItem(items[index], updated)
        return items
      }
      return [updated, ...items]
    }
    const keepSelected = this.visibleItemId === updated.id
    const keepForLists = this.matchesServerFilters(updated)
    this.pageItems = this.itemPageKey(updated) === pageKey() && (keepForLists || keepSelected)
      ? upsert(this.pageItems.filter(item => item.id !== updated.id))
      : this.pageItems.filter(item => item.id !== updated.id)
    this.projectItems = keepForLists || keepSelected
      ? upsert(this.projectItems.filter(item => item.id !== updated.id))
      : this.projectItems.filter(item => item.id !== updated.id)
  }

  private async updateItem(itemId: string, patch: Omit<UpdateTestingItemPayload, 'projectId'>): Promise<void> {
    try {
      const updated = await this.api.updateItem(itemId, { projectId: this.requireProjectId(), ...patch })
      this.updateLocalItem(updated)
      await this.revalidateAfterMutation(itemId)
      this.syncUi()
      this.config.onItemUpdate?.(updated)
    } catch (error) {
      if (this.handleAuthError(error)) return
      throw error
    }
  }

  private async addComment(itemId: string, message: string): Promise<void> {
    try {
      const updated = await this.api.addComment(itemId, { projectId: this.requireProjectId(), message })
      this.updateLocalItem(updated)
      await this.revalidateAfterMutation(itemId)
      this.syncUi()
      this.config.onItemUpdate?.(updated)
    } catch (error) {
      if (this.handleAuthError(error)) return
      throw error
    }
  }

  private async editComment(itemId: string, commentId: string, message: string): Promise<void> {
    try {
      const updated = await this.api.editComment(itemId, { projectId: this.requireProjectId(), commentId, message })
      this.updateLocalItem(updated)
      await this.revalidateAfterMutation(itemId)
      this.syncUi()
      this.config.onItemUpdate?.(updated)
    } catch (error) {
      if (this.handleAuthError(error)) return
      throw error
    }
  }

  private async resolveItem(itemId: string, resolution: string): Promise<void> {
    try {
      const updated = await this.api.resolveItem(itemId, { projectId: this.requireProjectId(), resolution })
      this.updateLocalItem(updated)
      await this.revalidateAfterMutation(itemId)
      this.syncUi()
      this.config.onItemResolve?.(updated)
    } catch (error) {
      if (this.handleAuthError(error)) return
      throw error
    }
  }

  private setAnnotating(active: boolean): void {
    if (active && !this.toolEnabled) return
    this.isAnnotating = active
    this.toolbar?.setAnnotateActive(active)
    if (active) {
      this.attachAnnotateListeners()
    } else {
      this.detachAnnotateListeners()
      this.syncVisibleItemHighlight()
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId)
        this.rafId = null
      }
    }
    this.updateFreezeStyles()
  }

  private setFrozen(frozen: boolean): void {
    this.isFrozen = frozen
    this.toolbar?.setFreezeActive(frozen)
    if (frozen) {
      this.updateFreezeStyles()
      this.freezePopovers()
      for (const evt of this.freezeBlockEvents) window.addEventListener(evt, this.boundFreezeClick, true)
      window.addEventListener('submit', this.boundFreezeSubmit, true)
      for (const evt of this.freezePassiveEvents) window.addEventListener(evt, this.boundFreezePassive, true)
    } else {
      this.frozenStyleEl?.remove()
      this.frozenStyleEl = null
      this.unfreezePopovers()
      for (const evt of this.freezeBlockEvents) window.removeEventListener(evt, this.boundFreezeClick, true)
      window.removeEventListener('submit', this.boundFreezeSubmit, true)
      for (const evt of this.freezePassiveEvents) window.removeEventListener(evt, this.boundFreezePassive, true)
    }
  }

  private freezePopovers(): void {
    this.frozenPopovers = []
    const openSelector = ':popover-open, .\\:popover-open'
    document.querySelectorAll('[popover]').forEach(el => {
      const htmlEl = el as HTMLElement
      const val = htmlEl.getAttribute('popover') ?? ''
      let isOpen = false
      try { isOpen = htmlEl.matches(openSelector) } catch {
        try { isOpen = htmlEl.matches('.\\:popover-open') } catch {}
      }
      if (!isOpen) return
      const rect = htmlEl.getBoundingClientRect()
      this.frozenPopovers.push({ el: htmlEl, original: val })
      htmlEl.removeAttribute('popover')
      htmlEl.style.setProperty('display', 'block', 'important')
      htmlEl.style.setProperty('position', 'fixed', 'important')
      htmlEl.style.setProperty('z-index', '2147483644', 'important')
      htmlEl.style.setProperty('top', `${rect.top}px`, 'important')
      htmlEl.style.setProperty('left', `${rect.left}px`, 'important')
      htmlEl.style.setProperty('width', `${rect.width}px`, 'important')
      htmlEl.classList.add(':popover-open')
    })
  }

  private unfreezePopovers(): void {
    for (const { el, original } of this.frozenPopovers) {
      for (const prop of ['display', 'position', 'z-index', 'top', 'left', 'width']) el.style.removeProperty(prop)
      el.classList.remove(':popover-open')
      el.setAttribute('popover', original || 'auto')
    }
    this.frozenPopovers = []
  }

  private freezeBlockEvents = ['click', 'mousedown', 'pointerdown', 'pointerup', 'mouseup', 'touchstart', 'touchend', 'auxclick'] as const
  private freezePassiveEvents = ['focusin', 'focusout', 'blur', 'pointerleave', 'mouseleave', 'mouseout'] as const

  private updateFreezeStyles(): void {
    if (!this.isFrozen) return
    this.frozenStyleEl?.remove()
    this.frozenStyleEl = document.createElement('style')
    this.frozenStyleEl.id = 'visual-qa-freeze'
    const pointerBlock = this.isAnnotating ? '' : `
      a[href], a[wire\\:navigate], [wire\\:click], [wire\\:navigate],
      [x-on\\:click], [@click], [v-on\\:click], [onclick],
      button, input[type="submit"], select, [role="button"], [role="link"],
      [tabindex] {
        pointer-events: none !important;
        cursor: not-allowed !important;
      }
    `
    this.frozenStyleEl.textContent = `
      *, *::before, *::after {
        animation-play-state: paused !important;
        transition: none !important;
      }
      video { filter: none !important; }
      ${pointerBlock}
    `
    document.head.appendChild(this.frozenStyleEl)
  }

  private boundFreezeClick = (event: Event): void => {
    if (this.isInstruckt(event.target)) return
    if (this.isAnnotating && event.type === 'click') return
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
  }

  private boundFreezeSubmit = (event: Event): void => {
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
  }

  private boundFreezePassive = (event: Event): void => {
    event.stopPropagation()
    event.stopImmediatePropagation()
  }

  private boundMouseMove = (event: MouseEvent): void => {
    if (this.highlightLocked) return
    this.pendingMouseTarget = event.target as Element
    if (this.rafId === null) {
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null
        if (this.highlightLocked) return
        if (this.pendingMouseTarget && !this.isInstruckt(this.pendingMouseTarget)) this.highlight?.show(this.pendingMouseTarget)
        else this.highlight?.hide()
      })
    }
  }

  private boundMouseLeave = (): void => {
    if (this.highlightLocked) return
    this.highlight?.hide()
  }

  private boundAnnotateBlock = (event: Event): void => {
    if (this.isInstruckt(event.target)) return
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
  }

  private boundClick = (event: MouseEvent): void => {
    const target = event.target as Element
    if (this.isInstruckt(target)) return
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()

    const selectedText = window.getSelection()?.toString().trim() || undefined
    const elementPath = getElementSelector(target)
    const elementName = getElementName(target)
    const elementLabel = getElementLabel(target)
    const cssClasses = getCssClasses(target)
    const nearbyText = getNearbyText(target) || undefined
    const boundingBox = getPageBoundingBox(target)

    this.highlight?.show(target)
    this.highlightLocked = true
    void this.detectFramework(target).then(framework => {
      const pending: PendingAnnotation = {
        element: target,
        elementPath,
        elementName,
        elementLabel,
        cssClasses,
        boundingBox,
        x: event.clientX,
        y: event.clientY,
        selectedText,
        nearbyText,
        framework: framework ?? undefined,
      }
      this.showAnnotationPopup(pending)
    })
  }

  private showAnnotationPopup(pending: PendingAnnotation): void {
    this.popup?.showNew(pending, this.members, this.currentUser, {
      onSubmit: async (result, openAfter) => {
        this.highlightLocked = false
        this.highlight?.hide()
        await this.submitItem(pending, result, openAfter)
      },
      onCancel: () => {
        this.highlightLocked = false
        this.highlight?.hide()
      },
    })
  }

  private attachAnnotateListeners(): void {
    document.addEventListener('mousemove', this.boundMouseMove)
    document.addEventListener('mouseleave', this.boundMouseLeave)
    for (const evt of ['mousedown', 'pointerdown'] as const) window.addEventListener(evt, this.boundAnnotateBlock, true)
    window.addEventListener('click', this.boundClick, true)
  }

  private detachAnnotateListeners(): void {
    document.removeEventListener('mousemove', this.boundMouseMove)
    document.removeEventListener('mouseleave', this.boundMouseLeave)
    for (const evt of ['mousedown', 'pointerdown'] as const) window.removeEventListener(evt, this.boundAnnotateBlock, true)
    window.removeEventListener('click', this.boundClick, true)
  }

  private isInstruckt(el: EventTarget | null): boolean {
    if (!el || !(el instanceof Element)) return false
    return el.closest('[data-visual-qa]') !== null
  }

  private async startRegionCapture(): Promise<void> {
    const wasAnnotating = this.isAnnotating
    if (wasAnnotating) this.setAnnotating(false)
    const rect = await selectRegion()
    if (!rect) {
      if (wasAnnotating) this.setAnnotating(true)
      return
    }
    const screenshot = await captureRegion(rect)
    if (!screenshot) {
      if (wasAnnotating) this.setAnnotating(true)
      return
    }

    const centerX = rect.x + rect.width / 2
    const centerY = rect.y + rect.height / 2
    const target = document.elementFromPoint(centerX, centerY) ?? document.body
    const framework = await this.detectFramework(target)
    const pending: PendingAnnotation = {
      element: target,
      elementPath: getElementSelector(target),
      elementName: getElementName(target),
      elementLabel: getElementLabel(target),
      cssClasses: getCssClasses(target),
      boundingBox: getPageBoundingBox(target),
      x: centerX,
      y: centerY,
      nearbyText: getNearbyText(target) || undefined,
      screenshot,
      framework: framework ?? undefined,
    }
    this.showAnnotationPopup(pending)
  }

  private async detectFramework(el: Element): Promise<FrameworkContext | null> {
    const adapters = this.config.adapters ?? []
    if (adapters.includes('livewire')) {
      const ctx = livewireAdapter.getContext(el)
      if (ctx) return ctx
    }
    const esAdapters = ['vue', 'svelte', 'react'] as const
    if (esAdapters.some(adapter => adapters.includes(adapter))) {
      try {
        const info = await resolveElementSource(el)
        if (info.source) {
          const stack: SourceFrame[] = info.stack.map(frame => ({
            filePath: frame.filePath,
            lineNumber: frame.lineNumber,
            columnNumber: frame.columnNumber,
            componentName: frame.componentName,
          }))
          let adapterCtx = null
          if (adapters.includes('vue')) adapterCtx = vueAdapter.getContext(el)
          if (!adapterCtx && adapters.includes('svelte')) adapterCtx = svelteAdapter.getContext(el)
          if (!adapterCtx && adapters.includes('react')) adapterCtx = reactAdapter.getContext(el)
          if (adapterCtx) {
            return {
              ...adapterCtx,
              component: info.componentName ?? adapterCtx.component,
              source_file: info.source.filePath,
              source_line: info.source.lineNumber ?? adapterCtx.source_line,
              source_column: info.source.columnNumber ?? undefined,
              component_stack: stack.length > 0 ? stack : undefined,
            }
          }
          return {
            framework: 'react',
            component: info.componentName ?? 'Component',
            source_file: info.source.filePath,
            source_line: info.source.lineNumber ?? undefined,
            source_column: info.source.columnNumber ?? undefined,
            component_stack: stack.length > 0 ? stack : undefined,
          }
        }
      } catch {}
    }
    if (adapters.includes('vue')) {
      const ctx = vueAdapter.getContext(el)
      if (ctx) return ctx
    }
    if (adapters.includes('svelte')) {
      const ctx = svelteAdapter.getContext(el)
      if (ctx) return ctx
    }
    if (adapters.includes('react')) {
      const ctx = reactAdapter.getContext(el)
      if (ctx) return ctx
    }
    if (adapters.includes('blade')) {
      const ctx = bladeAdapter.getContext(el)
      if (ctx) return ctx
    }
    return null
  }

  private buildCreatePayload(pending: PendingAnnotation, result: PopupCreateItemResult): CreateTestingItemPayload {
    const userAgent = navigator.userAgent
    const browser = Bowser.getParser(userAgent, (navigator as Navigator & {
      userAgentData?: {
        brands?: Array<{ brand: string; version: string }>
        mobile?: boolean
        platform?: string
      }
    }).userAgentData)
      .getBrowserName()
      .toLowerCase() || undefined
    return {
      projectId: this.requireProjectId(),
      title: result.title,
      description: result.description,
      priority: result.priority,
      severity: result.severity,
      screenshot: result.screenshot,
      url: currentPageUrl(),
      domain: currentDomain(),
      element: pending.elementName,
      selectorPath: pending.elementPath,
      coordinates: {
        x: pending.x + window.scrollX,
        y: pending.y + window.scrollY,
        pagePath: pageKey(),
        anchorXRatio: pending.boundingBox.width > 0
          ? Math.min(1, Math.max(0, (pending.x + window.scrollX - pending.boundingBox.x) / pending.boundingBox.width))
          : undefined,
        anchorYRatio: pending.boundingBox.height > 0
          ? Math.min(1, Math.max(0, (pending.y + window.scrollY - pending.boundingBox.y) / pending.boundingBox.height))
          : undefined,
      },
      consoleLog: this.consoleCapture.recent(),
      networkRequests: this.networkCapture.recent(),
      userAgent,
      browser,
      device: /mobile|iphone|android/i.test(userAgent) ? 'Mobile' : 'Desktop',
      os: navigator.platform || undefined,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      assignedTo: result.assignedTo,
    }
  }

  private async submitItem(pending: PendingAnnotation, result: PopupCreateItemResult, openAfter: boolean): Promise<void> {
    this.setAnnotating(false)
    try {
      if (!this.currentUser) await this.loadCurrentUser()
      const createdItem = await this.api.createItem(this.buildCreatePayload(pending, result))
      this.updateLocalItem(createdItem)
      if (openAfter) this.setVisibleItem(createdItem.id)
      this.syncUi()
      this.config.onItemAdd?.(createdItem)
      this.copyItems(createdItem.id)
    } catch (error) {
      if (this.handleAuthError(error)) return
      throw error
    }
  }

  private onMarkerClick(marker: MarkerRecord): void {
    this.setVisibleItem(marker.id)
  }

  private toggleItemDrawer(active: boolean): void {
    if (!this.toolEnabled) return
    if (active) {
      this.drawer?.show(this.drawerItems(), this.members, this.visibleItemId, this.config.endpoint, this.assigneeFilter, this.pageScope, this.statusFilter, this.createdByFilter, this.sortOrder, this.currentUser?.name ?? 'Me', this.initialDataLoading || this.listReloadLoading, this.detailLoadingItemId, this.detailErrorItemId)
    } else {
      this.visibleItemId = null
      this.drawer?.close()
      this.syncVisibleItemHighlight()
    }
  }

  private async popOutDrawer(): Promise<void> {
    if (!this.toolEnabled || !this.drawer) return
    if (this.drawer.isPipActive()) return
    if (!this.drawer.isOpen()) {
      this.drawer.show(this.drawerItems(), this.members, this.visibleItemId, this.config.endpoint, this.assigneeFilter, this.pageScope, this.statusFilter, this.createdByFilter, this.sortOrder, this.currentUser?.name ?? 'Me', this.initialDataLoading || this.listReloadLoading, this.detailLoadingItemId, this.detailErrorItemId)
      this.toolbar?.setIssuesActive(true)
    }
    const opened = await this.drawer.openPip()
    if (opened) this.toolbar?.setIssuesActive(true)
  }

  private redockAfterPip(): void {
    if (!this.drawer || !this.toolEnabled) {
      this.visibleItemId = null
      this.toolbar?.setIssuesActive(false)
      this.syncVisibleItemHighlight()
      return
    }
    this.toolbar?.setIssuesActive(true)
    this.drawer.show(this.drawerItems(), this.members, this.visibleItemId, this.config.endpoint, this.assigneeFilter, this.pageScope, this.statusFilter, this.createdByFilter, this.sortOrder, this.currentUser?.name ?? 'Me', this.initialDataLoading || this.listReloadLoading, this.detailLoadingItemId, this.detailErrorItemId)
    this.syncVisibleItemHighlight()
  }

  private async clearPage(): Promise<void> {
    const items = this.actionableItems(this.pageItems)
    for (const item of items) await this.updateItem(item.id, { status: 'closed' })
  }

  private async clearEverything(): Promise<void> {
    const items = this.actionableItems(this.allItems())
    for (const item of items) await this.updateItem(item.id, { status: 'closed' })
  }

  private onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return
    if (target.closest('[contenteditable="true"]')) return
    if (this.isInstruckt(target)) return

    const keys = this.config.keys ?? {}
    if (matchesKeyBinding(event, keys.toggle ?? 'alt+shift+v')) {
      if (event.repeat) return
      event.preventDefault()
      this.setToolEnabled(!this.toolEnabled)
      return
    }
    const noMod = !event.metaKey && !event.ctrlKey && !event.altKey
    if (!this.toolEnabled) return
    if (event.key === (keys.annotate ?? 'a') && noMod) this.setAnnotating(!this.isAnnotating)
    if (event.key === (keys.items ?? 'i') && noMod) this.toggleItemDrawer(!this.drawer?.isOpen())
    if (event.key === (keys.freeze ?? 'f') && noMod) this.setFrozen(!this.isFrozen)
    if (event.key === (keys.screenshot ?? 'c') && noMod) void this.startRegionCapture()
    if (event.key === (keys.copy ?? 'y') && noMod) void this.copyToClipboard(true)
    if (event.key === (keys.clearPage ?? 'x') && noMod) void this.clearPage()
    if (event.key === (keys.popOut ?? 'p') && noMod) void this.popOutDrawer()
    if (event.key === 'Escape') {
      if (this.isAnnotating) this.setAnnotating(false)
      if (this.isFrozen) this.setFrozen(false)
    }
  }

  private copyItems(itemId?: string): void {
    void this.copyToClipboard(false, itemId)
  }

  private compactNetworkEntries(entries: NonNullable<TestingItem['networkRequests']>): NonNullable<TestingItem['networkRequests']> {
    const important = entries.filter(entry => entry.method === 'POST' || (entry.status ?? 0) >= 400 || entry.error)
    if (important.length > 0) return important
    return entries.slice(-5)
  }

  private async copyToClipboard(fallback: boolean, itemId?: string): Promise<boolean> {
    const markdown = this.exportMarkdown(itemId)
    if (window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(markdown)
        return true
      } catch {
        if (!fallback) return false
      }
    }
    if (fallback) {
      try {
        const el = document.createElement('textarea')
        el.value = markdown
        el.style.cssText = 'position:fixed;left:-9999px'
        document.body.appendChild(el)
        el.select()
        const copied = document.execCommand('copy')
        el.remove()
        return copied
      } catch {
        return false
      }
    }
    return false
  }

  private personLabel(first?: string, last?: string): string | null {
    const name = [first, last].filter(Boolean).join(' ').trim()
    return name || null
  }

  private itemAssigneeLabel(item: TestingItem): string {
    if (!item.assignedTo) return 'Unassigned'
    return item.assignedName ?? this.personLabel(item.assignedFirst, item.assignedLast) ?? `Staff #${item.assignedTo}`
  }

  private itemCreatorLabel(item: TestingItem): string | null {
    return item.createdName ?? this.personLabel(item.createdFirst, item.createdLast)
  }

  private exportableItems(): TestingItem[] {
    return this.drawerItems(null)
  }

  private exportPageScopeLabel(): string {
    return this.pageScope === 'page' ? 'Current page' : 'All pages'
  }

  private exportStatusFilterLabel(): string {
    return titleCase(this.statusFilter)
  }

  private exportAssigneeFilterLabel(): string | number {
    if (this.assigneeFilter === 'me' || this.assigneeFilter === 'all' || this.assigneeFilter === 'unassigned') return this.assigneeFilter
    return this.assigneeFilter
  }

  private exportCreatedByFilterLabel(): string | number {
    if (this.createdByFilter === 'me' || this.createdByFilter === 'all') return this.createdByFilter
    return this.createdByFilter
  }

  private formatCoordinates(item: TestingItem): string | null {
    if (!item.coordinates) return null
    const xValue = `${Math.round(item.coordinates.x)}px`
    const y = `${Math.round(item.coordinates.y)}px`
    const path = item.coordinates.pagePath ? ` on ${item.coordinates.pagePath}` : ''
    return `${xValue} from left, ${y} from top${path}`
  }

  private formatConsoleEntries(item: TestingItem): string[] {
    return (item.consoleLog ?? []).map(entry => {
      const source = entry.source ? ` [${entry.source}]` : ''
      return `- ${titleCase(entry.level)}${source}: ${entry.message}`
    })
  }

  private formatNetworkEntries(item: TestingItem): string[] {
    const entries = item.networkRequests ? this.compactNetworkEntries(item.networkRequests) : []
    return entries.flatMap(entry => {
      const status = entry.status ? ` ${entry.status}` : ''
      const error = entry.error ? ` (${entry.error})` : ''
      const duration = `${Math.round(entry.durationMs)}ms`
      const lines = [`- ${entry.method} ${entry.url}${status} ${duration}${error}`.trim()]
      if (entry.requestBody) {
        lines.push('  request payload:')
        lines.push(...entry.requestBody.split('\n').map(line => `    ${line}`))
      }
      if (entry.responseBody) {
        lines.push('  response body:')
        lines.push(...entry.responseBody.split('\n').map(line => `    ${line}`))
      }
      return lines
    })
  }

  private itemMarkdown(item: TestingItem): string {
    const lines = [
      `# ${item.title}`,
      '',
      '## Summary',
      `- Status: ${titleCase(item.status)}`,
      `- Priority: ${titleCase(item.priority)}`,
      `- Severity: ${titleCase(item.severity)}`,
      `- Page: ${item.url}`,
      `- Created: ${item.createdAt}`,
      `- Updated: ${item.updatedAt}`,
    ]

    lines.push(`- Assignee: ${this.itemAssigneeLabel(item)}`)
    const reporter = this.itemCreatorLabel(item)
    if (reporter) lines.push(`- Reported by: ${reporter}`)
    if (item.domain) lines.push(`- Domain: ${item.domain}`)
    if (item.prLink) lines.push(`- PR Link: ${item.prLink}`)
    const coordinates = this.formatCoordinates(item)
    if (coordinates) lines.push(`- Coordinates: ${coordinates}`)
    if (item.element) lines.push(`- Element: ${item.element}`)
    if (item.selectorPath) lines.push(`- Selector: ${item.selectorPath}`)
    if (item.browser) lines.push(`- Browser: ${item.browser}`)
    if (item.device) lines.push(`- Device: ${item.device}`)
    if (item.os) lines.push(`- OS: ${item.os}`)
    if (item.viewportWidth && item.viewportHeight) lines.push(`- Viewport: ${item.viewportWidth}×${item.viewportHeight}`)

    lines.push('', '## Description', item.description?.trim() || 'Not provided.', '')

    if (item.resolution) {
      lines.push('## Resolution Note', item.resolution, '')
    }

    lines.push('## Comments')
    if (item.comments.length === 0) {
      lines.push('No comments yet.', '')
    } else {
      for (const comment of item.comments) {
        lines.push(`- ${comment.name} (${comment.createdAt}): ${comment.message}`)
      }
      lines.push('')
    }

    if (item.screenshot) {
      lines.push('## Screenshot', item.screenshot.startsWith('data:') ? 'Attached as inline image data.' : item.screenshot, '')
    }

    const consoleEntries = this.formatConsoleEntries(item)
    if (consoleEntries.length > 0) {
      lines.push('## Console Issues', ...consoleEntries, '')
    }

    const networkEntries = this.formatNetworkEntries(item)
    if (networkEntries.length > 0) {
      lines.push('## Network Activity', ...networkEntries, '')
    }

    lines.push('## Structured Context', '```json', JSON.stringify(this.serializeItem(item), null, 2), '```')
    return lines.join('\n').trim()
  }

  private projectMarkdown(items: TestingItem[]): string {
    const lines = [
      '# Visual QA Issues',
      '',
      `Project ID: ${this.config.projectId ?? 'Unknown'}`,
      `Generated: ${new Date().toISOString()}`,
      `Pages: ${this.exportPageScopeLabel()}`,
      `Status filter: ${this.exportStatusFilterLabel()}`,
      `Assignee filter: ${String(this.exportAssigneeFilterLabel())}`,
      `Created by filter: ${String(this.exportCreatedByFilterLabel())}`,
      '',
      '## Items',
    ]

    if (items.length === 0) {
      lines.push('No items available for the current filters.', '')
    } else {
      for (const item of items) {
        lines.push(`- [${item.id}] ${item.title} (${titleCase(item.status)}, ${titleCase(item.priority)}, ${titleCase(item.severity)})`)
        lines.push(`  Page: ${item.url}`)
        const reporter = this.itemCreatorLabel(item)
        if (reporter) lines.push(`  Reported by: ${reporter}`)
        lines.push(`  Created: ${item.createdAt}`)
        if (item.description?.trim()) lines.push(`  Description: ${item.description.trim()}`)
      }
      lines.push('')
    }

    lines.push('## Structured Context', '```json', JSON.stringify(this.exportPayload(), null, 2), '```')
    return lines.join('\n').trim()
  }

  private exportPayload(itemId?: string): Record<string, unknown> {
    const items = this.exportableItems()
    const selected = itemId ? this.allItems().find(item => item.id === itemId) ?? null : null
    if (selected) {
      return {
        scope: 'item',
        generated_at: new Date().toISOString(),
        item: this.serializeItem(selected),
      }
    }
    return {
      scope: 'list',
      generated_at: new Date().toISOString(),
      project_id: this.config.projectId ?? null,
      filters: {
        page_scope: this.exportPageScopeLabel(),
        status: this.exportStatusFilterLabel(),
        assigned_to: this.exportAssigneeFilterLabel(),
        created_by: this.exportCreatedByFilterLabel(),
      },
      items: items.map(item => this.serializeItem(item)),
    }
  }

  private serializeItem(item: TestingItem): Record<string, unknown> {
    return {
      id: item.id,
      project_id: item.projectId,
      title: item.title,
      description: item.description ?? null,
      url: item.url,
      domain: item.domain ?? null,
      page_path: this.itemPageKey(item),
      status: item.status,
      priority: item.priority,
      severity: item.severity,
      assigned_to: item.assignedTo,
        assigned_name: item.assignedName ?? ([item.assignedFirst, item.assignedLast].filter(Boolean).join(' ') || null),
        created_by: item.createdBy,
        created_name: item.createdName ?? ([item.createdFirst, item.createdLast].filter(Boolean).join(' ') || null),
      pr_link: item.prLink,
      resolution: item.resolution,
      element: item.element ?? null,
      selector_path: item.selectorPath ?? null,
      coordinates: item.coordinates ?? null,
      screenshot: item.screenshot ? {
        type: item.screenshot.startsWith('data:') ? 'inline_data_url' : 'stored_file',
        value: item.screenshot.startsWith('data:') ? item.screenshot : `${this.config.screenshotPath ?? ''}${item.screenshot}`,
      } : null,
      comments: item.comments.map(comment => ({
        id: comment.id,
        staffid: comment.staffid,
        name: comment.name,
        message: comment.message,
        created_at: comment.createdAt,
      })),
      console_log: item.consoleLog ?? [],
      network_requests: item.networkRequests ? this.compactNetworkEntries(item.networkRequests) : [],
      user_agent: item.userAgent ?? null,
      browser: item.browser ?? null,
      device: item.device ?? null,
      os: item.os ?? null,
      viewport_width: item.viewportWidth ?? null,
      viewport_height: item.viewportHeight ?? null,
      framework: item.framework ?? null,
      created_at: item.createdAt,
      updated_at: item.updatedAt,
    }
  }

  exportMarkdown(itemId?: string): string {
    if (itemId) {
      const item = this.allItems().find(entry => entry.id === itemId)
      if (item) return this.itemMarkdown(item)
    }
    return this.projectMarkdown(this.exportableItems())
  }

  getItems(): TestingItem[] { return [...this.allItems()] }

  destroy(): void {
    this.setAnnotating(false)
    this.setFrozen(false)
    document.removeEventListener('keydown', this.boundKeydown)
    document.removeEventListener('livewire:navigated', this.boundLivewireNavigated)
    document.removeEventListener('inertia:navigate', this.boundInertiaNavigate)
    window.removeEventListener('popstate', this.boundPopstate)
    history.pushState = this.originalPushState
    history.replaceState = this.originalReplaceState
    window.removeEventListener('scroll', this.boundReposition)
    window.removeEventListener('resize', this.boundReposition)
    this.toolbar?.destroy()
    this.highlight?.destroy()
    this.popup?.destroy()
    this.markers?.destroy()
    this.drawer?.destroy()
    this.consoleCapture.stop()
    this.networkCapture.stop()
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }
}
